/**
 * @file 資料のページ画像の一覧を取得する hook。
 *
 * @what: familyId / sourceId ごとに 1 回読む。空配列 = ページ画像を取り込んでいない。
 * @why: 問ごとの一覧 (scan_items) とは別に取り、どちらが入っているかで見せ方を決める。
 * @failure: 失敗は `error` (画面は 1 行で知らせる。詳細は console のみ)。
 */

import { useEffect, useState } from "react";
import { fetchSourcePages, type StudyPage } from "../api/pages";

export type SourcePagesState =
  | { status: "loading" }
  | { status: "ready"; pages: StudyPage[] }
  | { status: "error" };

export function useSourcePages(familyId: string, sourceId: string): SourcePagesState {
  const [state, setState] = useState<SourcePagesState>({ status: "loading" });

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: "loading" });
    fetchSourcePages(familyId, sourceId, controller.signal)
      .then((pages) => {
        if (!controller.signal.aborted) setState({ status: "ready", pages });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        console.error("[study] failed to load source pages", err);
        setState({ status: "error" });
      });
    return () => controller.abort();
  }, [familyId, sourceId]);

  return state;
}
